import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AfterViewInit, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { LoginService } from '@ebw-monorepo/login';
import { safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { toSafeImageBlobUrl } from '@ebw-monorepo/utils';
import { map, Subject } from 'rxjs';
import { debounceTime, pairwise, startWith, takeUntil } from 'rxjs/operators';
import { API_URLS, ApiUrlService } from '../../../../../../utils/src/lib/services/api-url.service';
import { LayerWMS, LayerWMTS } from '../../../models/layer.model';
import { ConfigSidebarService } from '../../../services/config-sidebar.service';
import { MapService } from './../../../services/map.service';
import { DraggableLegendComponent } from './draggable-legend/draggable-legend.component';
import { ActiveLayers, LayerConfigurator, TabContent } from './layer-configurator.model';
import { ENVIRONMENT_TOKEN, EnvironmentToken } from '@ebw-monorepo/utils';
import { CustomerEnum } from 'libs/ng-components/utils/src/lib/enums/customer.enum';

@Component({
  selector: 'ebw-layers-configurator',
  templateUrl: './ebw-layers-configurator.component.html',
  styleUrls: ['./ebw-layers-configurator.component.scss']
})
export class EbwLayersConfiguratorComponent implements OnInit, OnDestroy, AfterViewInit {
  /** Panel configuration structure */
  @Input() config: LayerConfigurator[] | any = null;
  /** Index of the tab to be selected */
  @Input() selectedTab: number = 0;
  formSelectedTab: FormControl;
  /** Active layers in the map */
  activeLayers: ActiveLayers[] = [];
  /** Layer state change event */
  @Output() openLegend: EventEmitter<any> = new EventEmitter();
  /** Array that contains all the forms in the panel */
  forms: FormGroup[] = [];
  /** Structure of the tabs present */
  visibleTabs: any[] = [];

  labelsTranslate = {
    // 'Catastale': 'map.sidebar.ebw_layers_configurator.cadastral',
    // 'Stato d\'uso': 'map.sidebar.ebw_layers_configurator.state_of_use',
    // 'Demaniale': 'map.sidebar.ebw_layers_configurator.state_property',
    // 'Demaniale d\'impianto': 'map.sidebar.ebw_layers_configurator.state_property_plant',
    // 'Ordinanze Autorità Marittime': 'map.sidebar.ebw_layers_configurator.maritime_authority_ordinances'
  };

  destroy$ = new Subject<void>();

  private layers: (LayerWMS | LayerWMTS)[];

  constructor(
    private dialog: MatDialog,
    private mapService: MapService,
    private _router: Router,
    private _configSidebarService: ConfigSidebarService,
    private _apiUrlService: ApiUrlService,
    private _httpClient: HttpClient,
    private _sanitizer: DomSanitizer,
    @Inject(ENVIRONMENT_TOKEN) private environment: EnvironmentToken
  ) {
    if (!this.config && this._router.getCurrentNavigation() && this._router.getCurrentNavigation().extras) {
      const { config } = this._router.getCurrentNavigation().extras.state;
      this.config = config;
    } else if (!this.config && !this._router.getCurrentNavigation() && history.state) {
      const { config } = history.state;
      this.config = config;
    }
  }

  ngOnInit() {
    this.formSelectedTab = new FormControl(this.selectedTab);
    this.activeLayers = this.mapService.getConfigurationActiveLayers(this.config, this.mapService.mapZoom);
    this.configManager();
    this.layersManager();
    this.getActiveLayers();

    this.mapService.mapConfigurationLoaded$.subscribe((config) => {
      if (!config) {
        return;
      }
      this.layers = [...config.wmsLayers, ...config.wmtsLayers];
      this.configurationLegendsLayer();
    });
  }

  // --------------------------------------------------------------------------------------------------

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --------------------------------------------------------------------------------------------------

  ngAfterViewInit() {
    this.formSelectedTab = new FormControl(this.selectedTab);

    // At each check I manage the actions of the valueChanges in order to intercept
    // change of state and react accordingly
    this.forms.forEach((form, index) => {
      form.valueChanges.pipe(startWith(form.value), pairwise()).subscribe(([old, changed]) => {
        for (const key in changed) {
          changed[key].map((value, i) => {
            if (typeof value === 'boolean') {
              return value;
            }
            const allSameValues = value.children.every((c) => !!c);
            const parentControl = this.forms[index].get(key) as FormArray;
            const parentGroup = parentControl.at(i) as FormGroup;
            const parentValue = parentGroup?.value?.parent;
            parentValue !== allSameValues && parentGroup.patchValue({ parent: allSameValues }, { emitEvent: false });
            return allSameValues ? { ...value, parent: allSameValues } : value;
          });
        }

        const objCompareResult = this.objCompare(old, changed);
        if (!objCompareResult.res) {
          setTimeout(() => {
            this.updateStateGroup(form, index);
            this.updateStateSession(
              index,
              objCompareResult.groupName,
              objCompareResult.layerIndex, // Passiamo l'indice
              objCompareResult.childrenIndex, // Passiamo l'indice
              objCompareResult.oldValue,
              objCompareResult.newValue
            );
          }, 5);
        }
      });
    });
  }

  // --------------------------------------------------------------------------------------------------

  /**
   * Get the form control of the children
   * @param tab_i
   * @param group_name
   * @param iterator_i
   * @param iterator_j
   */
  getChildrenControl(tab_i: number, group_name: string, iterator_i, iterator_j): FormControl {
    const abstractControl = this.forms[tab_i].get(group_name) as FormGroup;
    const value = abstractControl.controls[iterator_i] as FormGroup;
    const children = value.controls.children as FormArray;
    return children.controls[iterator_j] as FormControl;
  }

  // --------------------------------------------------------------------------------------------------

  /**
   * In ascolto sulle variazioni dello zoom per poter abilitare/disabilitare
   * il toggle di ogni layer.
   */
  getActiveLayers() {
    this.mapService.mapZoomChanged.pipe(takeUntil(this.destroy$), debounceTime(300)).subscribe({
      next: (zoom: number) => {
        // update the active layers list with the layers at the current zoom level
        this.activeLayers = this.mapService.getConfigurationActiveLayers(this.config, zoom);
      },
      error: (error) => console.error('[layersConfigurator][getActiveLayers]', error)
    });
  }

  // --------------------------------------------------------------------------------------------------

  /**
   * Function to check if a layer is active
   * @param value id of the layer
   * @returns {boolean} True or false depending on if the layer is active or not
   * @author Joshua Micheletti <j.micheletti@ebw.it>
   */
  isLayerActive(layerId: string): boolean {
    // find the layer object corresponding to the layer name
    const layer = this.activeLayers.find((el) => el.layerId === layerId);
    // if the layer is not in the active layers, return false
    if (!layer) {
      return false;
    }
    // return the value of the field 'disabled' in the layer
    return layer.disabled;
  }

  // --------------------------------------------------------------------------------------------------

  getControls(tab_i: number, group_name: string): { [key: string]: FormControl } {
    return (this.forms[tab_i].get(group_name) as FormGroup).controls as { [key: string]: FormControl };
  }

  // --------------------------------------------------------------------------------------------------

  private configManager() {
    // I go through the initial configuration and create form group and form control based on the type of control to implement
    this.config.forEach(async (tab) => {
      let tabCopy = safeJsonClone(tab);

      for (let j = 0; j < tab.content.length; j++) {
        const contentIndex = tab.content.findIndex((el) => {
          return el == tab.content[j];
        });

        for (let i = 0; i < tab.content[j].values.length; i++) {
          if (!this._configSidebarService.checkLayerVisibilitySync(tab.content[j].values[i])) {
            const index = tabCopy.content[j].values.findIndex((el) => {
              return el == tab.content[j].values[i];
            });

            tabCopy.content[contentIndex].values.splice(index, 1);
            tabCopy.content[contentIndex].labels.splice(index, 1);
            tabCopy.content[contentIndex].checked.splice(index, 1);
          }
        }

        if (tabCopy.content[contentIndex].values.length == 0) {
          tabCopy.content.splice(contentIndex, 1);
        }
      }

      this.visibleTabs.push(tabCopy);

      const obj = {};
      tabCopy.content.forEach((group) => {
        const childrenControls = group.values.map((layer, index) => {
          if (typeof layer === 'string') {
            return new FormControl(group.checked[index]); // Caso semplice senza figli
          } else {
            const children = layer.children.map((_, j) => new FormControl(group.checked[index].children[j]));
            return new FormGroup({ parent: new FormControl(false), children: new FormArray(children) }); // Caso con figli
          }
        });
        obj[group.groupName] = new FormArray(childrenControls);
      });
      this.forms.push(new FormGroup(obj));
    });
  }

  // --------------------------------------------------------------------------------------------------

  /**
   * Check if the parent control is checked
   * @param parentControl
   */
  isParentChecked(parentControl: FormControl): boolean {
    const childrenArray = parentControl.get('children') as FormArray;
    if (!childrenArray) return false;

    const every = childrenArray.controls.every((c) => c.value === true);
    // if (every !== parentControl.get('parent')?.value) {
    //   parentControl.patchValue({ parent: every }, { emitEvent: false });
    // }
    return every;
  }

  /**
   * Enable or disable the children of a parent control
   * @param tabIndex
   * @param groupName
   * @param fieldIndex
   */
  // enableOrDisableChildren(tabIndex: number, groupName: string, fieldIndex: number) {
  //   const form = this.forms[tabIndex];
  //   const group = form.get(groupName) as FormArray;

  //   if (!group) return;

  //   const parentControl = group.at(fieldIndex) as FormGroup;
  //   if (!parentControl) return;

  //   const parentValue = parentControl.get('parent')?.value;
  //   const childrenArray = parentControl.get('children') as FormArray;

  //   if (!childrenArray) return;

  //   const newValue = !parentValue;
  //   parentControl.patchValue({ parent: newValue }, { emitEvent: false });
  //   childrenArray.controls.forEach((control) => control.patchValue(newValue));
  //   // childrenArray.patchValue(childrenArray.controls.map(() => newValue));
  // }

  enableOrDisableChildren(tabIndex: number, groupName: string, fieldIndex: number) {
    const group = this.forms[tabIndex].get(groupName) as FormArray;
    const parentControl = group?.at(fieldIndex) as FormGroup;
    const childrenArray = parentControl?.get('children') as FormArray;
    if (!childrenArray) return;

    const allChecked = childrenArray.controls.every((c) => c.value === true);
    const newValue = !allChecked; // all checked → uncheck all; otherwise → check all
    parentControl.patchValue({ parent: newValue }, { emitEvent: false });
    childrenArray.controls.forEach((c) => c.patchValue(newValue));
  }

  /**
   * Update the visibility of the layers based on the configuration
   * @private
   */
  private layersManager() {
    if (this.activeLayers) {
      // Scorriamo i form, per ogni tab recuperiamo i gruppi e analizziamo i loro valori
      this.forms.forEach((tab, index) => {
        Object.keys(tab.value).forEach((groupName) => {
          const groupValues = tab.value[groupName];

          // If it's an array, the group is a series of checkboxes
          // Retrieve the values ​​from the default configuration
          const default_layers_name = this.visibleTabs[index].content.find((el: TabContent) => el.groupName == groupName).values;

          default_layers_name.forEach(async (element: string | any, elementIndex: number) => {
            if (element.children) {
              // 🔹 Caso con `children`
              const childLayers = element.children;

              // Trovo se almeno un child è visibile per determinare il parent
              const parentVisibility = childLayers.some((childLayer) => this.activeLayers.find((el) => el.layerId === childLayer && el.visibility));

              groupValues[elementIndex].parent = parentVisibility;

              // Imposto la visibilità dei figli
              groupValues[elementIndex].children = childLayers.map((childLayer) => {
                const match = this.activeLayers.find((el) => el.layerId === childLayer);
                return match ? match.visibility : false;
              });
            } else {
              // 🔹 Caso senza `children`
              const i = this.activeLayers.findIndex((el) => el.layerId == element);
              if (i >= 0) {
                groupValues[elementIndex] = this.activeLayers[i].visibility;
              }
            }
          });

          // Assegniamo i nuovi valori al form group
          this.forms[index].controls[groupName].setValue(groupValues, { emitEvent: false });
        });
      });
    }
  }

  /**
   * Update the state of the groups based on the values of the form
   * @param form
   * @param index
   * @private
   */
  private updateStateGroup(form: FormGroup, index: number) {
    const formValues = form.value;

    // I scroll through the groups and check on what they need to be enabled
    this.visibleTabs[index].content.forEach((group) => {
      const enabled: boolean[] = [];
      if (group.enabledBy) {
        group.enabledBy.forEach((layer) => {
          // I distinguish between group and enabling layer
          const groupName = layer.split('|')[0];
          const layerName = layer.split('|')[1];

          // I analyze the indicated value to distinguish between the types of groups
          const isObject = typeof formValues[groupName] === 'object';
          const isArray = Array.isArray(formValues[groupName]);

          if (!isArray && !isObject) {
            // If it is not an array or an object the group is a radio
            // I remember if the selected layer is the one I am looking for or not
            enabled.push(formValues[groupName] === layerName);
          } else if (isArray) {
            // If it's an array, the group is a series of checkboxes
            // I store if the layer is selected or not
            enabled.push(formValues[groupName][layerName]);
          } else if (!isArray && isObject) {
            // If it is an object the group is a select
            // I store if the selected layer is the one I am verifying or not
            enabled.push(formValues[groupName].value === layerName);
          }
        });
        // Check the type of operator to use
        // and check the saved values ​​by enabling or disabling groups
        if (group.enableOperator == 'OR') {
          if (!enabled.includes(true)) {
            form.get(group.group_name).disable();
          } else {
            form.get(group.group_name).enable();
          }
        } else if (group.enableOperator == 'AND') {
          if (!enabled.includes(false)) {
            form.get(group.group_name).disable();
          } else {
            form.get(group.group_name).enable();
          }
        }
      }
    });
  }

  /** Comparison between two objects*/
  objCompare(object1: any, object2: any) {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    const result = {
      res: true,
      groupName: '',
      layerIndex: -1, // Indice del valore modificato
      childrenIndex: -1, // Indice del figlio modificato (se applicabile)
      oldValue: null,
      newValue: null
    };

    if (keys1.length !== keys2.length) {
      result.res = false;
      return result;
    }

    for (const key of keys1) {
      const val1 = object1[key];
      const val2 = object2[key];

      if (!Array.isArray(val1) || !Array.isArray(val2)) {
        // 🔹 Caso 1: Modifica su un toggle normale
        if (val1 !== val2) {
          return { res: false, groupName: key, layerIndex: 0, childrenIndex: -1, oldValue: val1, newValue: val2 };
        }
      } else {
        for (let i = 0; i < val1.length; i++) {
          if (typeof val1[i] === 'boolean' && typeof val2[i] === 'boolean') {
            // 🔹 Caso 2: Modifica su un toggle senza figli
            if (val1[i] !== val2[i]) {
              return {
                res: false,
                groupName: key,
                layerIndex: i,
                childrenIndex: -1,
                oldValue: val1[i],
                newValue: val2[i]
              };
            }
          } else if (typeof val1[i] === 'object' && typeof val2[i] === 'object') {
            for (let j = 0; j < val1[i].children.length; j++) {
              if (val1[i].children[j] !== val2[i].children[j]) {
                // 🔹 Caso 3: Modifica su un figlio ('layer complesso')

                return {
                  res: false,
                  groupName: key,
                  layerIndex: i, // L'indice del parent
                  childrenIndex: j, // L'indice del figlio modificato
                  oldValue: { index: i, childIndex: j, value: val1[i].children[j] },
                  newValue: { index: i, childIndex: j, value: val2[i].children[j] }
                };
              }
            }
          }
        }
      }
    }

    return result;
  }

  /**
   * Update the session status
   * @param tabIndex
   * @param groupName
   * @param layerIndex
   * @param childrenIndex
   * @param oldValue
   * @param newValue
   * @private
   */
  private updateStateSession(tabIndex: number, groupName: string, layerIndex: number, childrenIndex: number, oldValue: any, newValue: any) {
    if (!groupName || groupName.length === 0) return;

    const tab = this.visibleTabs[tabIndex];
    if (!tab) return;

    const groupIndex = tab.content.findIndex((el) => el.groupName === groupName);
    if (groupIndex === -1) return;

    const group = tab.content[groupIndex];
    const layerId = group.values[layerIndex];

    if (typeof layerId === 'string') {
      // Gestione del caso semplice: un toggle normale
      const sessionIndex = this.activeLayers.findIndex((el) => el.layerId === layerId);
      if (sessionIndex >= 0) {
        this.activeLayers[sessionIndex].visibility = !this.activeLayers[sessionIndex].visibility;
      }
    } else if (typeof layerId === 'object' && layerId.children) {
      // Gestione del caso con sottofigli
      const parentControl = this.forms[tabIndex].get(groupName) as FormArray;
      const parentGroup = parentControl.at(oldValue.index) as FormGroup;
      const childrenArray = parentGroup.get('children') as FormArray;

      if (!childrenArray) return;

      if (childrenIndex >= 0) {
        // Controlliamo se il figlio è stato modificato
        const layer_attuale = this.activeLayers.find((el) => el.layerId === layerId.children[childrenIndex]);
        layer_attuale.visibility = newValue.value;
      } else {
        // childrenArray.controls.forEach((child, i) => {
        //   const sessionChild = this.activeLayers.find((el) => el.layer === layerName.children[i]);
        //   if (sessionChild) {
        //     sessionChild.visibility = newValue.parent;
        //   }
        // });
      }
    }

    this.confirm();
  }

  /**
   * Update the visibility of the layers in the map
   */
  confirm() {
    if (this.activeLayers) {
      this.activeLayers.forEach((sessionLayer) => {
        this.mapService.setLayerVisibility(sessionLayer.layerId, sessionLayer.visibility);
      });
    }
  }

  openDialog(title: string, url: string): void {
    this.dialog.open(DraggableLegendComponent, {
      hasBackdrop: false,
      data: { title, image: url }
    });
  }

  /**
   * Popola la proprietà `styles` all’interno dei gruppi delle tab visibili,
   * associando a ciascun layer (o sotto-layer) lo style WMS corrispondente.
   *
   * Logica:
   * - Costruisce una mappa veloce `id -> style` a partire da `this.layers`.
   * - Per ogni gruppo:
   *   - Se `value` è una stringa (UUID layer), recupera lo style dalla mappa.
   *   - Se `value` è un oggetto con `children`, crea una struttura parallela
   *     contenente gli style dei figli.
   * - Il risultato viene salvato in `group.styles`, mantenendo la stessa
   *   struttura di `group.values`.
   *
   * Note:
   * - Se uno style non è trovato, viene impostato a `null`.
   * - La funzione modifica direttamente gli oggetti presenti in `visibleTabs`.
   *
   * @returns {void}
   */
  configurationLegendsLayer() {
    // lookup veloce per map id -> style
    const styleMap = new Map(this.layers.map((l) => [l.id, l.ui_params?.style ?? null]));

    this.visibleTabs.forEach((sessionLayer) => {
      sessionLayer.content.forEach((group) => {
        // costruiamo styles parallelo a values
        group.styles = group.values.map((value) => {
          // caso UUID semplice
          if (typeof value === 'string') {
            return styleMap.get(value) ?? null;
          }
          // caso oggetto con children
          if (typeof value === 'object' && Array.isArray(value.children)) {
            return {
              name: value.name,
              children: value.children.map((childId: string) => styleMap.get(childId) ?? null)
            };
          }
          return null;
        });
      });
    });
  }

  /**
   * Apre un dialog draggable contenente la legenda di uno style WMS.
   *
   * Flusso:
   * - Se lo style è vuoto o non valido, la funzione termina.
   * - Costruisce l’URL dell’endpoint per il recupero della legenda.
   * - Effettua una chiamata HTTP GET autenticata che restituisce un'immagine (blob).
   * - Converte il blob in object URL sicuro tramite `DomSanitizer`.
   * - Apre tutto nella dialog
   *
   * @param {string} style - Nome dello style WMS di cui recuperare la legenda.
   * @param {string} label - Titolo da mostrare nel dialog della legenda.
   * @returns {void}
   */
  openDialogLegend(style: string, label: string): void {
    if (!style || style === '') return;

    const imageUrl = this._apiUrlService.createUrl(API_URLS.MAP.LAYER.GET_LEGEND);
    const opts = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY),
        user: sessionStorage.getItem(LoginService.USER_ID_KEY)
      })
    };

    this._httpClient
      .get(`${imageUrl}?style=${style}`, { ...opts, responseType: 'blob' })
      .pipe(map((val) => toSafeImageBlobUrl(this._sanitizer, val)))
      .subscribe((res) => {
        this.dialog.open(DraggableLegendComponent, {
          hasBackdrop: false,
          data: { title: label, image: res }
        });
      });
  }

  isParentIndeterminate(parentControl: FormControl): boolean {
    const children = parentControl.get('children') as FormArray;
    if (!children) return false;
    const values = children.controls.map((c) => c.value === true);
    return values.some((v) => v) && !values.every((v) => v);
  }

  isExpanded(elementIndex: Number){
    const fastweb = this.environment.customer == CustomerEnum.Fastweb;
    return fastweb ? false : elementIndex === 0;
  }
}
